import { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { Container, Form, Nav } from "react-bootstrap";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { defaultChartConfig, donutChartConfig } from "./chartConfigs";
import { getCompanies, getProductTypes, getSpecies, getDashboardData } from "./StaffDashboardApi";
import { toast } from "react-toastify";
import "./StaffDashboard.css";
import Button from "../../../components/atoms/button/Button";
ChartJS.register(ArcElement, Tooltip, Legend);

const STATUS_CONFIG = [
  { label: "Requested", color: "#919191", key: "requestedCount" },
  { label: "Received", color: "#745AF2", key: "receivedCount" },
  { label: "In-Progress", color: "#009EFB", key: "inProgressCount" },
  { label: "Completed", color: "#55CE63", key: "completedCount" },
  { label: "Due > 10 Days", color: "#DE942A", key: "countGreaterThanTenDays" },
  { label: "Due > 14 Days", color: "#FF5747", key: "countGreaterThanFourteenDays" },
];

const tabConfig = [
  { label: "Dashboard", key: "Dashboard" },
];

const LAB_PRODUCT_KEYS = [
  "Bacillus", "Bacitracin Methylene Disalicylate", "Carbadox", "Chlortetracycline",
  "Decoquinate", "Florfenicol", "Lasalocid", "Lincomycin",
  "Melengestrol Acetate", "Morantel tartrate", "Nicarbazin", "Ormetoprim",
  "Oxytetracycline", "Poloxalene", "Pyrantel tartrate", "Quillaja saponins",
  "Ractopamine", "Robenidine", "Salinomycin", "Semduramicin",
  "Sulfadimethoxine", "Sulfamethazine", "Virginiamycin", "Zoalene"
];

const PRODUCT_COLORS = [
  "#413E3F", "#53B5F0", "#FC5F82", "#3F4145",
  "#E8CB49", "#8288FA", "#FF5C4F", "#61E083",
  "#008D99", "#FFA14F", "#59E0DF", "#FCB14F",
  "#838383", "#7766CC", "#77CC77", "#4477AA",
  "#59E0DF", "#AA4466", "#666622", "#229966",
  "#664422", "#442266", "#224466", "#665544"
];

const SampleStatus = ({ data = {} }) => {
  const chartData = {
    labels: STATUS_CONFIG.map((s) => s.label),
    datasets: [
      {
        data: STATUS_CONFIG.map((s) => data[s.key] ?? 0),
        backgroundColor: STATUS_CONFIG.map((s) => s.color),
        borderWidth: 0,
      },
    ],
  };

  const options = donutChartConfig;
  return (
    <div className="staff-dashboard-custom-card staff-dashboard-custom-border-radius-sm">
      <div className="staff-dashboard-custom-card-body">
        <div className="staff-dashboard-chart-header staff-dashboard-left">
          <h6 className="staff-dashboard-chart-title">Sample Status</h6>
        </div>
        <div className="staff-dashboard-chart-container">
          <div className="staff-dashboard-donutWrap">
            <Doughnut data={chartData} options={options} />
          </div>
          <div className="staff-dashboard-statusChips">
            {STATUS_CONFIG.map((s) => (
              <div className="staff-dashboard-chip" key={s.key}>
                <span className="staff-dashboard-chipCount" style={{ backgroundColor: s.color }}>
                  {data[s.key] ?? 0}
                </span>
                <span className="staff-dashboard-chipLabel">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

function LabTrendChart({ data = [] }) {
  const categories = data.length > 0
    ? data.map(item => {
      const date = new Date(item.date);
      return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear().toString().substr(-2)}`;
    })
    : [];

  const series = LAB_PRODUCT_KEYS.map((type, index) => ({
    name: type,
    data: data.length > 0
      ? data.map(item => item.productTypeCounts?.[type] || 0)
      : [0],
    color: PRODUCT_COLORS[index]
  }));

  const options = {
    ...defaultChartConfig,
    chart: {
      ...defaultChartConfig.chart,
      type: "bar",
      stacked: true,
      toolbar: {
        show: false
      },
      animations: {
        enabled: true
      }
    },
    dataLabels: {
      enabled: true,
      enabledOnSeries: undefined,
      formatter: function (val, opts) {
        if (opts.seriesIndex === opts.w.config.series.length - 1) {
          const total = opts.w.globals.stackedSeriesTotals[opts.dataPointIndex];
          return total > 0 ? total : '';
        }
        return '';
      },
      textAnchor: 'middle',
      distributed: false,
      offsetX: 0,
      offsetY: -10,
      style: {
        fontSize: '12px',
        fontFamily: 'Helvetica, Arial, sans-serif',
        fontWeight: 'bold',
        colors: ['#495057']
      }
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '60%',
        borderRadius: 0,
        dataLabels: {
          total: {
            enabled: true,
            offsetX: 0,
            offsetY: -10,
            style: {
              color: '#495057',
              fontSize: '12px',
              fontWeight: 'bold',
              fontFamily: 'Helvetica, Arial, sans-serif'
            },
            formatter: function (val, opts) {
              return val > 0 ? val : '';
            }
          }
        }
      }
    },
    xaxis: {
      categories,
      tickPlacement: "on",
      position: "bottom",
      labels: {
        rotate: -45,
        style: {
          fontSize: "12px",
          colors: "#495057",
          fontWeight: 400
        },
        rotateAlways: true,
        offsetY: 5,
        formatter: function (value) {
          if (!value || value === "1" || value === 1) return "";
          return value;
        }
      },
      axisBorder: { show: true },
      axisTicks: { show: true }
    },
    yaxis: {
      min: 0,
      max: 125,
      tickAmount: 5,
      forceNiceScale: false,
      decimalsInFloat: 0,
      labels: {
        style: { fontSize: "12px", colors: "#495057", fontWeight: 400 },
        formatter: (val) => Math.round(val)
      },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    legend: {
      ...defaultChartConfig.legend,
      show: true,
      position: "bottom",
      horizontalAlign: "left",
      onItemClick: { toggleDataSeries: true },
      offsetY: 8,
      markers: {
        width: 10,
        height: 10,
        radius: 0,
        offsetX: -5
      }
    },
    colors: PRODUCT_COLORS,
    stroke: {
      width: 0,
      colors: ['transparent']
    },
    fill: {
      opacity: 1
    }
  };

  return (
    <div className="staff-dashboard-custom-card staff-dashboard-lab-trend-chart">
      <div className="staff-dashboard-chart-header staff-dashboard-center">
        <h6 className="staff-dashboard-chart-title">Lab Product Trend</h6>
      </div>
      <div className="staff-dashboard-chart-container">
        <ReactApexChart options={options} series={series} type="bar" height={350} />
      </div>
    </div>
  );
}

function CompanySamplesChart({ data }) {
  const series = [{ name: "Product Sample", data: data.map((d) => d.samples) }];
  const categories = data.map((d) => d.company);

  const options = {
    ...defaultChartConfig,
    chart: {
      ...defaultChartConfig.chart,
      type: "bar",
      toolbar: { show: false },
      zoom: { enabled: false },
      animations: { enabled: true }
    },
    dataLabels: {
      enabled: true,
      offsetY: -20,
      style: {
        fontSize: '12px',
        fontWeight: 500,
        colors: ['#495057']
      },
      formatter: (val) => (val > 0 ? val : ""),
      position: 'top'
    },
    xaxis: {
      categories,
      tickPlacement: "on",
      position: "bottom",
      labels: {
        style: {
          fontSize: "12px",
          colors: "#495057",
          fontWeight: 400
        },
        offsetY: 5,
        formatter: function (value) {
          if (!value || value === "1" || value === 1) return "";
          return value;
        }
      },
      axisBorder: { show: true },
      axisTicks: { show: true }
    },
    yaxis: {
      min: 0,
      max: 15,
      tickAmount: 3,
      forceNiceScale: false,
      decimalsInFloat: 0,
      title: {
        text: "Total Sample Completed",
        style: { fontSize: "12px", fontWeight: 500 }
      },
      labels: {
        style: { fontSize: "12px", colors: "#495057", fontWeight: 400 },
        formatter: (val) => Math.round(val)
      },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    colors: ["#5AB3EF"],
    legend: {
      ...defaultChartConfig.legend,
      show: true,
      position: "bottom",
      horizontalAlign: "center",
      offsetY: 6
    },
    plotOptions: {
      bar: {
        borderRadius: 0,
        columnWidth: '50%',
        barHeight: '70%',
        dataLabels: {
          position: 'top'
        }
      }
    },
    states: {
      hover: {
        filter: {
          type: 'darken',
          value: 0.9
        }
      }
    }
  };

  return (
    <div className="staff-dashboard-customer-chart-wrap">
      <ReactApexChart options={options} series={series} type="bar" height={300} />
      <div className="staff-dashboard-product-sample-legend">
        <span className="staff-dashboard-product-sample-dot"></span>
        <span className="staff-dashboard-product-sample-text">Product Sample</span>
      </div>
    </div>
  );
}

const StaffDashboard = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [productTypes, setProductTypes] = useState([]);
  const [species, setSpecies] = useState([]);
  const [dashboardData, setDashboardData] = useState({
    sampleStatusPie: {
      requestedCount: 0,
      receivedCount: 0,
      inProgressCount: 0,
      completedCount: 0,
      countGreaterThanTenDays: 0,
      countGreaterThanFourteenDays: 0
    }
  });
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(() => {
    const today = new Date();
    const oneMonthAgo = new Date(today);
    oneMonthAgo.setMonth(today.getMonth() - 1);

    return {
      companyId: '',
      productType: '',
      species: '',
      fromDate: oneMonthAgo.toISOString().split('T')[0],
      toDate: today.toISOString().split('T')[0]
    };
  });

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setFilters(prev => ({ ...prev, toDate: today }));
  }, []);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const companiesRes = await getCompanies();
        if (companiesRes.success) {
          setCompanies(companiesRes.results || []);
        }
      } catch (error) {
        console.error("Error fetching companies:", error);
        toast.error("Failed to load companies");
      }
    };

    fetchCompanies();
  }, []);

  useEffect(() => {
    const fetchSpecies = async () => {
      try {
        const response = await getSpecies();
        if (response.success && Array.isArray(response.results)) {
          setSpecies(response.results);
        } else {
          console.error("Invalid species response:", response);
          setSpecies([]);
        }
      } catch (error) {
        console.error("Error fetching species:", error);
        toast.error("Failed to load species");
        setSpecies([]);
      }
    };

    fetchSpecies();
  }, []);

  useEffect(() => {
    const fetchProductTypes = async () => {
      try {
        const response = await getProductTypes(filters.companyId);
        if (response.success && Array.isArray(response.results)) {
          setProductTypes(response.results);
        } else {
          console.error("Invalid product types response:", response);
          setProductTypes([]);
        }
      } catch (error) {
        console.error("Error fetching product types:", error);
        toast.error("Failed to load product types");
        setProductTypes([]);
      }
    };

    fetchProductTypes();
  }, [filters.companyId]);

  useEffect(() => {
    const initialFetch = async () => {
      setLoading(true);
      try {
        const initialFilters = {
          companyId: 0,
          productType: 0,
          species: 0,
          fromDate: filters.fromDate,
          toDate: filters.toDate
        };

        const response = await getDashboardData(initialFilters);
        if (response.success) {
          setDashboardData(response);
        } else {
          toast.error("Failed to load dashboard data");
        }
      } catch (error) {
        console.error("Error fetching initial dashboard data:", error);
        toast.error("Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };

    initialFetch();
  }, []);

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const searchFilters = {
        ...filters,
        companyId: filters.companyId || 0,
        productType: filters.productType || 0,
        species: filters.species || 0
      };

      const response = await getDashboardData(searchFilters);
      if (response.success) {
        setDashboardData(response);
      } else {
        toast.error("Failed to load dashboard data");
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Container fluid className="second-tab-container">
        <Nav
          variant="underline"
          activeKey="dashboard"
          className="flex-nowrap nav-underline"
        >
          {tabConfig.map((tab) => (
            <Nav.Item key={tab.key}>
              <Nav.Link
                eventKey={tab.key}
                className="default-font-styles"
                active
              >
                {tab.label}
              </Nav.Link>
            </Nav.Item>
          ))}
        </Nav>
      </Container>
      <div className="staff-dashboard-bg">
        <div className="staff-dashboard-filter-section staff-dashboard-custom-border-radius-sm">
          <Form className="staff-dashboard-custom-form-flex staff-dashboard-custom-align-items-center staff-dashboard-custom-gap-3" onSubmit={handleSearch}>
            <div className="staff-dashboard-custom-form-flex staff-dashboard-custom-align-items-center">
              <Form.Label className="staff-dashboard-custom-form-label staff-dashboard-filter-label">Company</Form.Label>
              <Form.Select
                className="staff-dashboard-filter-select staff-dashboard-custom-border-radius-md"
                value={filters.companyId}
                onChange={(e) => {
                  const value = e.target.value;
                  handleFilterChange('companyId', value);
                  const company = companies.find(c => c.companyId.toString() === value);
                  setSelectedCompany(company || null);
                }}
              >
                <option value="">Select</option>
                {companies.map((company) => (
                  <option key={company.companyId} value={company.companyId}>
                    {company.name}
                  </option>
                ))}
              </Form.Select>
            </div>

            <div className="staff-dashboard-custom-form-flex staff-dashboard-custom-align-items-center">
              <Form.Label className="staff-dashboard-custom-form-label staff-dashboard-filter-label">Drug</Form.Label>
              <Form.Select
                className="staff-dashboard-filter-select staff-dashboard-drug-select"
                value={filters.productType}
                onChange={(e) => handleFilterChange('productType', e.target.value)}
              >
                <option value="">Select</option>
                {productTypes.map((type) => (
                  <option key={type.productTypeId} value={type.productTypeId}>
                    {type.name}
                  </option>
                ))}
              </Form.Select>
            </div>

            <div className="staff-dashboard-custom-form-flex staff-dashboard-custom-align-items-center">
              <Form.Label className="staff-dashboard-custom-form-label staff-dashboard-filter-label">Species</Form.Label>
              <Form.Select
                className="staff-dashboard-filter-select"
                value={filters.species}
                onChange={(e) => handleFilterChange('species', e.target.value)}
              >
                <option value="">Select</option>
                {species.map((specie) => (
                  <option key={specie.speciesId} value={specie.speciesId}>
                    {specie.name}
                  </option>
                ))}
              </Form.Select>
            </div>

            <div className="staff-dashboard-custom-form-flex staff-dashboard-custom-align-items-center">
              <Form.Label className="staff-dashboard-custom-form-label staff-dashboard-filter-label">From</Form.Label>
              <Form.Control
                type="date"
                className="staff-dashboard-date-input staff-dashboard-custom-border-radius-md"
                value={filters.fromDate}
                onChange={(e) => handleFilterChange('fromDate', e.target.value)}
              />
            </div>
            <div className="staff-dashboard-custom-form-flex staff-dashboard-custom-align-items-center">
              <Form.Label className="staff-dashboard-custom-form-label staff-dashboard-filter-label">To</Form.Label>
              <Form.Control
                key={`to-date-${filters.toDate}`}
                type="date"
                className="staff-dashboard-date-input"
                value={filters.toDate}
                onChange={(e) => handleFilterChange('toDate', e.target.value)}
              />
            </div>

            <Button
              className="btn-common outlined-active-btn staff-dashboard-btn-md-size default-font-style"
              variant="outlined"
              type="submit"
            >
              Search
            </Button>
          </Form>
        </div>

        <div className="staff-dashboard-charts-row">
          <LabTrendChart data={dashboardData.labProductTrend?.labTrends || []} />
          <SampleStatus data={dashboardData.sampleStatusPie} />
        </div>

        <div className="staff-dashboard-charts-row staff-dashboard-single-row-chart">
          <div className="staff-dashboard-custom-card staff-dashboard-customer-samples-chart">
            <div className="staff-dashboard-chart-header staff-dashboard-center">
              <h6 className="staff-dashboard-chart-title">Company Samples Completed</h6>
            </div>
            <div className="staff-dashboard-chart-container">
              <CompanySamplesChart data={dashboardData.customerSamplesCompleted?.companySamples || []} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffDashboard;
